<script setup>
import IconComponent from '../../icons/index.vue';

const props = defineProps({
  template: {
    type: String,
    default: 'filled'
  },
  label: {
    type: String,
    default: ''
  },
  icon: {
    type: String,
    default: ''
  },
  disabled: {
    type: Boolean,
    default: false
  },
  size: {
    type: String,
    default: '24'
  },
  labelFor: {
    type: String,
    default: undefined
  },
  buttonSize: {
    type: String,
    default: 'medium',
    validator: (value) => {
      return ['large', 'medium', 'small', 'tiny'].includes(value);
    },
  },
  customStyle: {
    type: Object,
    default: undefined
  }
});
const emit = defineEmits(['click']);

const COLORS = {
  "filled": "#ffffff",
  "filled-neutral": "#ffffff",
  "filled-transparent": "#5E2F78",
  "elevated": "rgba(94, 47, 120, 1)",
  "outlined": "#5E2F78",
  "text-neutral": "rgba(29, 27, 32, 1)",
  "outlined-neutral": "rgba(29, 27, 32, 1)",
  "text": "#5E2F78",
  "text-only": "#5E2F78",
  "text-error": "rgba(186, 26, 26, 1)",
  "tonal": "#001E2F",
  "tonal-error": "rgba(65, 0, 2, 1)",
  "icon-disabled": "rgba(29, 27, 32, 1)",
};

const getIconColor = (template) => {
  if (props.disabled) {
    return COLORS['icon-disabled'];
  }
  return COLORS[template];
};

const handleClick = (event) => {
  emit('click', event)
};
</script>

<template>
  <button :class="`pge-button pge-button-${template}`" @click="handleClick" :disabled="disabled"
          :style="[customStyle]">
    <label :for="labelFor" class="pge-button-content" :class="buttonSize">
      <span v-if="icon" class="pge-icon">
        <icon-component :icon="icon" :color="getIconColor(template)" :size="size" />
      </span>
      <span v-if="label" :aria-label="label">{{ label }}</span>
    </label>
  </button>
</template>

<style lang="scss" scoped>
.pge-button {
  position: relative;
  display: inline-flex;
  height: 40px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  border-radius: 100px;
  border: 0;
  padding: 0;
  cursor: pointer;
  transition: background-color 200ms cubic-bezier(0.2, 0.0, 0, 1.0),
              box-shadow 200ms cubic-bezier(0.2, 0.0, 0, 1.0);

  &.pge-button-content {
    cursor: pointer;
    text-align: center;
    font-family: 'Lato', sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px;
    letter-spacing: 0.1px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex: 1 0 0;
    align-self: stretch;
    gap: 8px;

    &.large {
      padding: 12px 38px;
    }

    &.medium {
      padding: 10px 26px;
    }

    &.small {
      padding: 10px 14px;
    }

    &.tiny {
      padding: 10px;
    }

    label {
      cursor: pointer;
    }

    label:empty+.pge-icon {
      margin-right: -8px;
    }

    label:empty {
      display: none;
    }

    .pge-icon {
      display: flex;
    }
  }


  &:disabled {
    cursor: not-allowed !important;

    label {
      cursor: not-allowed !important;
    }
  }

  .pge-icon {
    display: flex;
  }

  label {
    cursor: pointer;
  }
}

.pge-button-outlined {
  background-color: transparent !important;
  border: 1px solid rgba(121, 116, 126, 1) !important;

  .pge-icon,
  label {
    color: #5E2F78;
  }

  &:hover {
    background-color: rgba(94, 47, 120, 0.08) !important;
    border-color: rgba(121, 116, 126, 1) !important;
  }

  &:active {
    background-color: rgba(94, 47, 120, 0.12) !important;
    border-color: rgba(121, 116, 126, 1) !important;
  }

  &:disabled {
    background-color: rgba(29, 27, 32, 0.12) !important;
    border-color: rgba(29, 27, 32, 0.12) !important;

    .pge-icon,
    label {
      color: rgba(29, 27, 32, 1) !important;
    }
  }
}

.pge-button-filled {
  background: #5E2F78;

  .pge-icon,
  label {
    color: #ffffff;
  }

  &:hover {
    cursor: pointer;
    box-shadow: 0px 1px 3px 1px rgba(0, 0, 0, 0.15), 0px 1px 2px 0px rgba(0, 0, 0, 0.30);
  }

  &:active {
    box-shadow: unset;
  }

  &:focus {
    background: #5E2F78;
    color: #ffffff;
  }

  &:disabled {
    background: #1D1B201F;
    cursor: not-allowed;
    box-shadow: none;

    .pge-icon,
    label {
      color: #1D1B20;
    }
  }
}

.pge-button-filled-transparent {
  background: transparent;
  border-radius: 100px;
  border: var(--spacing-0, 1px) solid var(--XL-M-Theme-sys-dark-outline, #978E98);

  .pge-icon,
  label {
    color: #5E2F78;
  }
}

.pge-button-text {
  background-color: #ffffff !important;
  border: 1px solid transparent !important;

  .pge-icon,
  label {
    color: #5E2F78;
  }

  &:hover {
    background-color: rgba(94, 47, 120, 0.08) !important;
    border-color: transparent !important;
  }

  &:active {
    background-color: rgba(94, 47, 120, 0.12) !important;
    border-color: transparent !important;
  }

  &:disabled {
    background-color: #ffffff !important;
    border-color: transparent !important;

    .pge-icon,
    label {
      color: rgba(29, 27, 32, 1) !important;
    }
  }
}

.pge-button-text-only {
  background-color: transparent !important;
  border: 1px solid transparent !important;

  .pge-icon,
  label {
    color: #5E2F78;
  }

  &:hover {
    background-color: rgba(94, 47, 120, 0.08) !important;
    border-color: transparent !important;
  }

  &:active {
    background-color: rgba(94, 47, 120, 0.12) !important;
    border-color: transparent !important;
  }

  &:disabled {
    background-color: transparent !important;
    border-color: transparent !important;

    .pge-icon,
    label {
      color: rgba(29, 27, 32, 1) !important;
    }
  }
}

.pge-button-text-neutral {
  background-color: #ffffff !important;
  border: 1px solid transparent !important;

  .pge-icon,
  label {
    color: rgba(29, 27, 32, 1);
  }

  &:hover {
    background-color: rgba(94, 47, 120, 0.08) !important;
    border-color: transparent !important;
  }

  &:active {
    background-color: rgba(94, 47, 120, 0.12) !important;
    border-color: transparent !important;
  }

  &:disabled {
    background-color: #ffffff !important;
    border-color: transparent !important;

    .pge-icon,
    label {
      color: rgba(29, 27, 32, 1) !important;
    }
  }
}

.pge-button-text-error {
  background-color: transparent !important;
  border: 1px solid transparent !important;

  .pge-icon,
  label {
    color: rgba(186, 26, 26, 1) !important;
  }

  &:hover {
    background-color: rgba(186, 26, 26, 0.08) !important;
  }

  &:active {
    background-color: rgba(186, 26, 26, 0.12) !important;
  }

  &:disabled {
    background-color: #ffffff !important;
    border-color: transparent !important;

    .pge-icon,
    label {
      color: rgba(29, 27, 32, 1) !important;
    }
  }
}
</style>
